import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";

import useDelete from "../../api/useDelete";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import ErrorPage from "../Components/ErrorPage";
import IncomeAdd from "./IncomeAdd";
import IncomeEdit from "./IncomeEdit";

import { formatJalaliDateTime } from "../utils/dateFormat";
const IncomeIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["income", searchTerm, page, rowsPerPage],
    `shop/incomes/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("shop/incomes", token);

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const incomes = data?.results || data || [];
  const totalCount = data?.count || incomes.length;

  if (isError) {
    return <ErrorPage message={t("income.failedToLoadIncomes")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("income.incomeList")}</Typography>
        <Button variant="contained" onClick={() => setOpenAdd(true)}>
          {t("income.addIncome")}
        </Button>
      </Box>

      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("income.dataLoadedOn")} {formatJalaliDateTime()}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("income.searchIncomes")}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 750 }} aria-label="income table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("income.id")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("income.branch")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("income.account")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("income.amount")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("income.currency")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("income.exchangeRate")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("income.convertedAmount")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("income.incomeDate")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("income.notes")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("income.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {Array.from(new Array(10)).map((__, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : incomes.map((income, index) => (
                  <TableRow key={income.id} hover>
                    <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{income.shop_name || "--"}</TableCell>
                    <TableCell>{income?.account_details?.name || "--"}</TableCell>
                    <TableCell>{income.amount || "--"}</TableCell>
                    <TableCell>{income?.currency_details?.title || "--"}</TableCell>
                    <TableCell>{income.exchange_rate || "--"}</TableCell>
                    <TableCell>{income.converted_amount || "--"}</TableCell>
                    <TableCell>
                      {income.income_date ? formatJalaliDateTime(income.income_date) : "--"}
                    </TableCell>
                    <TableCell>{income.notes || "--"}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setEditData(income);
                          setOpenEdit(true);
                        }}
                      >
                        {t("income.edit")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(income.id)}
                      >
                        {t("income.delete")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container justifyContent="center" alignItems="center" padding="10px">
        <Box display="flex" alignItems="center">
          <Typography>{t("income.rowsPerPage")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
          >
            <Select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={(_, newPage) => setPage(newPage)}
          variant="outlined"
          color="primary"
        />
      </Grid>

      {openAdd && <IncomeAdd open={openAdd} onClose={() => setOpenAdd(false)} token={token} />}
      {openEdit && (
        <IncomeEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}

      <ConfirmDialog />
    </Box>
  );
};

export default IncomeIndex;
